import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, setUnauthorizedHandler } from '../api/client';
import { refreshEntitlements } from '../subscription/offers';
import { translate } from '../i18n/i18n';
import { resetTwoFactorPrompt } from '../settings/TwoFactorPrompt';
import {
  clear,
  currentAccess,
  onSignedOutElsewhere,
  setTokens,
  who,
  type TokenResponse,
} from './session';

type AuthUser = {
  email: string | null;
  displayName: string | null;
};

type AuthState = {
  token: string | null;
  user: AuthUser | null;
  /**
   * WHAT THE SUBSCRIPTION IS, ASKED SEPARATELY.
   *
   * It used to arrive with the sign-in response, because this app checked the
   * password itself. The token is minted by the realm now, which knows nothing
   * about who has paid — so this is fetched from our own API once there is a
   * token to fetch it with. Null means "not known yet", which is not the same
   * as "not subscribed" and must not be treated as it.
   */
  subscriptionStatus: string | null;
  /**
   * Whether asking for it failed.
   *
   * WITHOUT THIS THERE IS NO DIFFERENCE BETWEEN "not asked yet" AND "asked and
   * it went wrong", and the guard that reads it waited forever for an answer
   * that was never coming — a blank page with no error and no way out.
   */
  subscriptionFailed: boolean;
  ready: boolean;
};

export type RegisterResult = { email: string; next: string };

type AuthApi = AuthState & {
  login: (email: string, password: string, keepOnDevice: boolean) => Promise<{ challenge: string } | null>;
  loginWithCode: (challenge: string, code: string, keepOnDevice: boolean) => Promise<void>;
  emailLoginCode: (challenge: string) => Promise<void>;
  loginWithSchool: (code: string, state: string, keepOnDevice: boolean) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<RegisterResult>;
  logout: () => void;
  setSubscriptionStatus: (status: string) => void;
  /** Ask again. Safe to call repeatedly; used to recover from a failed load. */
  refreshSubscription: () => Promise<void>;
};

const Ctx = createContext<AuthApi | null>(null);

type StatusResponse = { status: string };

function held(): Pick<AuthState, 'token' | 'user'> {
  const access = currentAccess();
  if (!access) return { token: null, user: null };
  const w = who();
  return { token: access, user: { email: w?.email ?? null, displayName: w?.name ?? null } };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    token: null,
    user: null,
    subscriptionStatus: null,
    subscriptionFailed: false,
    ready: false,
  });

  const loadStatus = useCallback(async () => {
    try {
      const s = await api<StatusResponse>('/subscription/status', { token: true });
      setState((prev) => (prev.token
        ? { ...prev, subscriptionStatus: s.status, subscriptionFailed: false }
        : prev));
    } catch {
      /* STILL NOT GUESSED, BUT NO LONGER SWALLOWED. Assuming ACTIVE would hand
         out the product and assuming inactive would push a paying supervisor at
         the checkout page over a dropped connection — so the status stays
         unknown and the failure is recorded, which is what lets the screen say
         so and offer to try again instead of rendering nothing at all. */
      setState((prev) => (prev.token ? { ...prev, subscriptionFailed: true } : prev));
    }
  }, []);

  useEffect(() => {
    const h = held();
    setState({ ...h, subscriptionStatus: null, subscriptionFailed: false, ready: true });
    if (h.token) void loadStatus();
  }, [loadStatus]);

  const login = useCallback(async (email: string, password: string, keepOnDevice: boolean) => {
    const res = await api<TokenResponse & { challenge?: string | null }>('/auth/login', {
      method: 'POST',
      body: { email, password },
    });
    if (res.challenge) return { challenge: res.challenge };
    if (!setTokens(res, keepOnDevice)) throw new Error(translate('Sign-in failed'));
    setState({ ...held(), subscriptionStatus: null, subscriptionFailed: false, ready: true });
    await loadStatus();
    return null;
  }, [loadStatus]);

  const loginWithCode = useCallback(async (challenge: string, code: string, keepOnDevice: boolean) => {
    const res = await api<TokenResponse>('/auth/login/code', { method: 'POST', body: { challenge, code } });
    if (!setTokens(res, keepOnDevice)) throw new Error(translate('Sign-in failed'));
    setState({ ...held(), subscriptionStatus: null, subscriptionFailed: false, ready: true });
    await loadStatus();
  }, [loadStatus]);

  const loginWithSchool = useCallback(async (code: string, state: string, keepOnDevice: boolean) => {
    const res = await api<TokenResponse>('/auth/sso/complete', { method: 'POST', body: { code, state } });
    if (!setTokens(res, keepOnDevice)) throw new Error(translate('Sign-in failed'));
    setState({ ...held(), subscriptionStatus: null, subscriptionFailed: false, ready: true });
    await loadStatus();
  }, [loadStatus]);

  const emailLoginCode = useCallback(async (challenge: string) => {
    await api<void>('/auth/login/email-code', { method: 'POST', body: { challenge } });
  }, []);

  /**
   * DELIBERATELY DOES NOT SIGN ANYBODY IN. The realm will not issue a token for
   * an address that has not been confirmed, so the caller shows the "check your
   * email" step instead of a session that could not exist.
   */
  const register = useCallback(async (email: string, password: string, displayName: string) => {
    return await api<RegisterResult>('/auth/register', {
      method: 'POST',
      body: { email, password, displayName },
    });
  }, []);

  const logout = useCallback(() => {
    clear();
    resetTwoFactorPrompt();
    refreshEntitlements();
    setState({
      token: null, user: null, subscriptionStatus: null, subscriptionFailed: false, ready: true,
    });
  }, []);

  // When any authenticated API call is rejected with 401/403, drop the stale
  // session. The route guards then redirect to /login on the next render.
  useEffect(() => onSignedOutElsewhere(() => logout()), [logout]);

  useEffect(() => {
    setUnauthorizedHandler(() => logout());
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  const setSubscriptionStatus = useCallback((status: string) => {
    setState((prev) => ({ ...prev, subscriptionStatus: status, subscriptionFailed: false }));
  }, []);

  const refreshSubscription = useCallback(async () => {
    refreshEntitlements();
    setState((prev) => ({ ...prev, subscriptionFailed: false }));
    await loadStatus();
  }, [loadStatus]);

  const value = useMemo<AuthApi>(() => ({
    ...state,
    login,
    loginWithCode,
    emailLoginCode,
    loginWithSchool,
    register,
    logout,
    setSubscriptionStatus,
    refreshSubscription,
  }), [state, login, loginWithCode, emailLoginCode, loginWithSchool, register, logout, setSubscriptionStatus, refreshSubscription]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
